import {useCallback, useEffect, useRef, useState} from 'react';

import {webhookDeliveriesService} from '@/services/webhookDeliveriesService';
import type {WebhookDeliveryDetail} from '@/types/WebhookDelivery';
import {logRejection} from '@/utils/logRejection';

// The server returns 404 for both not-found and not-owned deliveries. Axios
// collapses the error to an Error whose message we can pattern-match, since no
// status code survives through the shared response interceptor.
const isNotFoundError = (error: Error): boolean =>
  /not.?found|404|no_?such/i.test(error.message);

// Fetches the detail for a single webhook delivery whenever `webhookId` changes.
// Pass null to stay idle (nothing selected → no fetch).
//
// Race guard mirrors useWebhookDeliveries: every request takes a monotonically
// increasing token; a response only commits if its token is still the latest in
// flight AND the component is still mounted. This stops a slow earlier
// selection's detail from overwriting the detail of a row clicked afterwards.
export default function useWebhookDeliveryDetail(
  businessId: string,
  webhookId: string | null,
) {
  const [detail, setDetail] = useState<WebhookDeliveryDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);

  const requestSeq = useRef(0);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchDetail = useCallback(
    async (id: string) => {
      const seq = ++requestSeq.current;
      setIsLoading(true);
      setError(null);
      setIsNotFound(false);
      try {
        const data =
          await webhookDeliveriesService.getBusinessWebhookDeliveryDetail(
            businessId,
            id,
          );
        // Ignore stale / superseded responses.
        if (!isMounted.current || seq !== requestSeq.current) {
          return;
        }
        setDetail(data);
      } catch (err) {
        if (!isMounted.current || seq !== requestSeq.current) {
          return;
        }
        console.error('Failed to fetch webhook delivery detail:', err);
        const typedError = err as Error;
        setError(typedError);
        setIsNotFound(isNotFoundError(typedError));
        setDetail(null);
      } finally {
        if (isMounted.current && seq === requestSeq.current) {
          setIsLoading(false);
        }
      }
    },
    [businessId],
  );

  useEffect(() => {
    if (!webhookId) {
      // Selection cleared: invalidate any in-flight fetch (ref bump only, no
      // setState — the consumer early-returns its empty state for a null id, so
      // leftover detail/loading state is never rendered).
      requestSeq.current++;
      return;
    }
    // `fetchDetail` catches into `error` state, so this only fires if that
    // catch or the finally itself throws. Log rather than swallow silently.
    fetchDetail(webhookId).catch(
      logRejection('Failed to load webhook delivery detail'),
    );
  }, [webhookId, fetchDetail]);

  return {detail, isLoading, error, isNotFound};
}
